/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import styled from 'styled-components';
import variables from '@splunk/themes/variables';
import Button, { ButtonProps } from '@splunk/react-ui/Button';
import MagnifierPlus from '@splunk/react-icons/MagnifierPlus';
import Cross from '@splunk/react-icons/Cross';
import MagnifierMinus from '@splunk/react-icons/MagnifierMinus';
import ChevronLeft from '@splunk/react-icons/ChevronLeft';
import ChevronRight from '@splunk/react-icons/ChevronRight';

import { CursorState } from './cursorPlugin';
import { Range } from './providers/RangeSelectionProvider';
import { PlotUtilities } from './renderers/types';
import { exists } from './utilities/utils';
import { useVizTheme } from './theme';

const SelectionBox = styled.div<{
    $leftPosition: number;
    $boxWidth: number;
}>`
    position: absolute;
    border: 1px solid #ccc;
    top: 0;
    bottom: 0;
    left: ${props => props.$leftPosition}px;
    width: ${props => props.$boxWidth}px;
`;

const SelectionOverlay = styled.div<{
    $leftPosition: number;
    $width: number;
    $backgroundColor: string;
    $borderColor: string;
}>`
    position: absolute;
    background: ${props => props.$backgroundColor};
    top: 0;
    bottom: 0;
    left: ${props => props.$leftPosition}px;
    width: ${props => props.$width}px;
`;

const LeftOverlay = styled(SelectionOverlay)`
    border-right: 1px solid ${props => props.$borderColor};
`;

const RightOverlay = styled.div<{ $leftPosition: number; $backgroundColor: string; $borderColor: string }>`
    position: absolute;
    background: ${props => props.$backgroundColor};
    border-left: 1px solid ${props => props.$borderColor};
    top: 0;
    bottom: 0;
    left: ${props => props.$leftPosition}px;
    right: 0;
`;

const ControlsContainer = styled.div<{ $leftPosition: number; $alignRight?: boolean }>`
    display: flex;
    flex-direction: column;
    position: absolute;
    ${props =>
        props.$alignRight
            ? `right: calc(100% - ${props.$leftPosition}px);`
            : `left: ${props.$leftPosition}px;`}
    top: 0;
    pointer-events: auto;
`;

type RangeSelectionProps = {
    plotUtilities?: PlotUtilities;
    cursorState?: CursorState;
    rangeSelection?: Range; // "_time" range represented by scaled x values (not snapped to points)
    onRangeSelection?: (range: Range, e: MouseEvent) => void;
    onRangeDeselection?: (e: MouseEvent) => void;
    onRangeZoom?: (range: Range, e: MouseEvent) => void;
    serverSideZoom?: boolean; // If true, use onRangeZoom callback; otherwise use client-side zoom
};

const StyledButtonWrapper = styled(Button)<ButtonProps>`
    background-color: ${variables.backgroundColorPopup} !important;
    margin-top: 8px;
    margin-left: 8px;
`;

const ResetZoomButton = styled(Button)<ButtonProps>`
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: ${variables.backgroundColorPopup} !important;
    z-index: 10;
`;

const PanButton = styled(Button)<ButtonProps>`
    position: absolute;
    bottom: 8px;
    background-color: ${variables.backgroundColorScrim} !important;
    opacity: 0.5;
    width: 24px;
    z-index: 10;
    padding: 8px;
    min-width: auto;
`;

const PanLeftButton = styled(PanButton)`
    left: 0px;
    bottom: -44px;
`;

const PanRightButton = styled(PanButton)`
    right: 0px;
    bottom: -44px;
`;

export const RangeSelection: React.FC<RangeSelectionProps> = React.memo(props => {
    const vizTheme = useVizTheme();
    const { overlayBackgroundColor } = vizTheme.rangeSelection;
    const { overlayBorderColor } = vizTheme.rangeSelection;

    // Use LOCAL state for zoom tracking to avoid re-render issues
    const [isZoomed, setIsZoomed] = React.useState(false);
    const [canPanLeft, setCanPanLeft] = React.useState(false);
    const [canPanRight, setCanPanRight] = React.useState(false);

    // Ref to measure the actual rendered width of the controls container
    const controlsRef = React.useRef<HTMLDivElement>(null);
    const [controlsWidth, setControlsWidth] = React.useState(0);
    const [containerWidth, setContainerWidth] = React.useState(0);

    React.useEffect(() => {
        if (controlsRef.current) {
            const newControlsWidth = controlsRef.current.offsetWidth;
            const parentWidth = controlsRef.current.parentElement?.offsetWidth ?? 0;
            if (newControlsWidth !== controlsWidth) {
                setControlsWidth(newControlsWidth);
            }
            if (parentWidth && parentWidth !== containerWidth) {
                setContainerWidth(parentWidth);
            }
        }
    });

    // Show reset zoom button if chart is zoomed (client-side only)
    const showResetZoom = isZoomed && !props.serverSideZoom;

    // Handle reset zoom
    const handleResetZoom = React.useCallback(() => {
        if (props.plotUtilities) {
            props.plotUtilities.resetScale('x');
            setIsZoomed(false);
            setCanPanLeft(false);
            setCanPanRight(false);
        }
    }, [props.plotUtilities]);

    // Handle pan left
    const handlePanLeft = React.useCallback(() => {
        if (props.plotUtilities) {
            const canStillPanLeft = props.plotUtilities.panLeft('x');
            setCanPanLeft(canStillPanLeft);
            setCanPanRight(true); // Can always pan right after panning left
        }
    }, [props.plotUtilities]);

    // Handle pan right
    const handlePanRight = React.useCallback(() => {
        if (props.plotUtilities) {
            const canStillPanRight = props.plotUtilities.panRight('x');
            setCanPanRight(canStillPanRight);
            setCanPanLeft(true); // Can always pan left after panning right
        }
    }, [props.plotUtilities]);

    // Handle server-side zoom
    const handleServerSideZoom = React.useCallback(
        (e: React.MouseEvent) => {
            if (!props.rangeSelection || !props.onRangeZoom) {
                return;
            }

            props.onRangeZoom(props.rangeSelection, e.nativeEvent);

            // Clear the range selection after zoom
            if (props.onRangeDeselection) {
                props.onRangeDeselection(e.nativeEvent);
            }
        },
        [props]
    );

    // Handle client-side zoom
    const handleClientSideZoom = React.useCallback(
        (e: React.MouseEvent) => {
            if (!props.rangeSelection || !props.plotUtilities) {
                return;
            }

            const { start, end } = props.rangeSelection;

            // Convert timestamps to seconds if they're in milliseconds
            const startTime = start > 1e12 ? start / 1000 : start;
            const endTime = end > 1e12 ? end / 1000 : end;

            // Notify parent component first if callback exists
            if (props.onRangeZoom) {
                props.onRangeZoom(props.rangeSelection, e.nativeEvent);
            }

            // Set the x-axis scale to zoom into the selected range
            props.plotUtilities.setScale('x', startTime, endTime);

            // Mark as zoomed (local state won't cause parent re-renders)
            setIsZoomed(true);
            // Initially, both pan buttons should be enabled
            setCanPanLeft(true);
            setCanPanRight(true);

            // Clear the range selection after zoom
            if (props.onRangeDeselection) {
                props.onRangeDeselection(e.nativeEvent);
            }
        },
        [props]
    );

    // Handle zoom button click
    const handleZoomClick = React.useCallback(
        (e: React.MouseEvent) => {
            if (!props.rangeSelection) {
                return;
            }

            // If serverSideZoom is true AND onRangeZoom callback is provided, use it (server-side zoom)
            if (props.serverSideZoom && props.onRangeZoom) {
                handleServerSideZoom(e);
            } else if (props.plotUtilities) {
                // Otherwise, perform client-side zoom using plotUtilities
                handleClientSideZoom(e);
            }
        },
        [props, handleServerSideZoom, handleClientSideZoom]
    );

    if (props.cursorState?.selection) {
        return (
            <>
                {showResetZoom && (
                    <ResetZoomButton label="Reset zoom" icon={<MagnifierMinus />} onClick={handleResetZoom} />
                )}
                {isZoomed && !props.serverSideZoom && (
                    <>
                        <PanLeftButton
                            icon={<ChevronLeft />}
                            onClick={handlePanLeft}
                            disabled={!canPanLeft}
                        />
                        <PanRightButton
                            icon={<ChevronRight />}
                            onClick={handlePanRight}
                            disabled={!canPanRight}
                        />
                    </>
                )}
                <SelectionBox
                    className="olly-viz-select-box"
                    $leftPosition={Math.max(0, props.cursorState.selection.left)}
                    $boxWidth={Math.max(0, props.cursorState.selection.width)}
                />
            </>
        );
    }

    if (!exists(props.rangeSelection) || !exists(props.plotUtilities)) {
        return showResetZoom ? (
            <>
                <ResetZoomButton label="Reset zoom" icon={<MagnifierMinus />} onClick={handleResetZoom} />
                {isZoomed && !props.serverSideZoom && (
                    <>
                        <PanLeftButton
                            icon={<ChevronLeft />}
                            onClick={handlePanLeft}
                            disabled={!canPanLeft}
                        />
                        <PanRightButton
                            icon={<ChevronRight />}
                            onClick={handlePanRight}
                            disabled={!canPanRight}
                        />
                    </>
                )}
            </>
        ) : null;
    }

    // Compute `left` and `width` using props.rangeSelection
    const left = props.plotUtilities.valToPos(props.rangeSelection.start, 'x');
    const right = props.plotUtilities.valToPos(props.rangeSelection.end, 'x');
    const width = right - left;

    // Determine if there is enough space to the right of the selection to show the controls.
    // containerWidth is measured from the parent element (rangeEl), which is in the same
    // scaled coordinate space as `left` and `width` from valToPos. Fall back to
    // dimensions.over.width only when the ref hasn't mounted yet.
    const plotWidth = containerWidth || props.plotUtilities.dimensions.over.width;
    const buttonsWidth = controlsWidth || 50;
    const alignRight = left + width + buttonsWidth > plotWidth;

    return (
        <>
            <LeftOverlay
                $leftPosition={0}
                $width={left}
                $backgroundColor={overlayBackgroundColor}
                $borderColor={overlayBorderColor}
            />
            <RightOverlay
                $leftPosition={left + width}
                $backgroundColor={overlayBackgroundColor}
                $borderColor={overlayBorderColor}
            />
            <ControlsContainer
                ref={controlsRef}
                data-testid="range-controls-container"
                data-test-align={alignRight ? 'left' : 'right'}
                $leftPosition={left + width}
                $alignRight={alignRight}
            >
                {props.onRangeDeselection && (
                    <StyledButtonWrapper
                        onClick={(e: React.MouseEvent): void => {
                            props.onRangeDeselection?.(e.nativeEvent);
                        }}
                        icon={<Cross />}
                    />
                )}
                <StyledButtonWrapper onClick={handleZoomClick} icon={<MagnifierPlus />} />
            </ControlsContainer>
        </>
    );
});
